"use client";

import {
  Badge,
  Box,
  Button,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { memo, type SyntheticEvent, useCallback, useRef } from "react";
import {
  type DeleteOriginsRequest,
  type MeiliOrigin,
  type MeiliOriginProcessedStatus,
} from "@/generated/fastapi";
import {
  type MeiliDocumentSearchHit,
  MEILI_SEARCH_CONFIG,
} from "@/features/search";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import { useSystemDesign } from "@/shared/system-design";
import { CONTENT_ITEM_FOCUS_VISIBLE_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import { CONTENT_ITEM_HOVER_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import ContentItemCard from "@/shared/ui/display/ContentItemCard";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";
import WithMenu from "@/shared/ui/actions/WithMenu";
import WithMenuItem from "@/shared/ui/actions/WithMenuItem";
import WithSwipeSelector from "@/shared/ui/gestures/WithSwipeSelector";
import { getHumanReadableTimestamp } from "@/shared/utils";
import useOriginItemActions, {
  type OriginItemAction,
  type ResolvedOriginAction,
} from "../hooks/useOriginItemActions";
import type { OriginDetailInput } from "../types";
import OriginActionIconButton from "./OriginActionIconButton";
import { getOriginActionColor } from "../config/originActionPresentation";
import useOriginItemGestures from "../hooks/useOriginItemGestures";
import OriginItemPreview from "./OriginItemPreview";

type OriginItemSelection = {
  isSelected: boolean;
  onToggle: () => void;
  humanReadable: string;
};

type OriginItemProps = {
  config: (typeof MEILI_SEARCH_CONFIG)[keyof typeof MEILI_SEARCH_CONFIG];
  hit: MeiliDocumentSearchHit;
  isInSearchContext?: boolean;
  surfaceVariant?: "default" | "backlog";
  onDeleteSearchResultAction?: (originId: string) => void;
  onDeleteOrigin: (
    request: DeleteOriginsRequest,
    onSuccess?: () => void,
  ) => Promise<unknown>;
  selection?: OriginItemSelection;
  primaryAction?: OriginItemAction;
  secondaryAction?: OriginItemAction;
  tertiaryAction?: OriginItemAction;
  editAction?: OriginItemAction;
  extendAction?: OriginItemAction;
  onOpenDetail?: (detail: OriginDetailInput) => void;
};

function renderMenuLabel(label: string, icon: GenericIconType, color?: string) {
  return (
    <HStack gap="2" align="center" color={color}>
      <GenericIcon icon={icon} size={iconSizePresets.menuItem} color={color} />
      <Text color={color}>{label}</Text>
    </HStack>
  );
}

const DESKTOP_INLINE_ACTION_KEYS = new Set(["preview", "extract", "extend"]);

function OriginItem({
  hit,
  config,
  isInSearchContext = true,
  surfaceVariant = "default",
  onDeleteSearchResultAction,
  onDeleteOrigin,
  selection,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  editAction,
  extendAction,
  onOpenDetail,
}: OriginItemProps) {
  const { isMobileDevice } = useSystemDesign();
  const origin = hit as MeiliOrigin;
  const isSelected = selection?.isSelected ?? false;
  const wasProcessed =
    origin.processed_status === ("PROCESSED" as MeiliOriginProcessedStatus);
  const formattedTimestamp = getHumanReadableTimestamp(origin.latest_at);
  const backgroundColor = isSelected
    ? "moveon.selection.bg"
    : surfaceVariant === "backlog"
      ? "moveon.surface.elevatedBg"
      : "moveon.surface.bg";
  const statusLabel = wasProcessed ? "Source shaped" : "Ready to shape";
  const statusPalette = wasProcessed ? "green" : "orange";
  const railColor = isSelected
    ? "moveon.selection.border"
    : wasProcessed
      ? "moveon.success"
      : "moveon.inactive";
  const canOpenDetail = Boolean(onOpenDetail);
  const suppressCardPreviewRef = useRef(false);
  const suppressCardPreviewTimeoutRef =
    useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopCardTogglePropagation = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
  }, []);
  const handleActionMenuOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (suppressCardPreviewTimeoutRef.current) {
        clearTimeout(suppressCardPreviewTimeoutRef.current);
        suppressCardPreviewTimeoutRef.current = null;
      }

      if (details.open) {
        suppressCardPreviewRef.current = true;
        return;
      }

      suppressCardPreviewTimeoutRef.current = setTimeout(() => {
        suppressCardPreviewRef.current = false;
        suppressCardPreviewTimeoutRef.current = null;
      }, 350);
    },
    [],
  );

  const resolvedActions = useOriginItemActions({
    editAction,
    extendAction,
    onDeleteOrigin,
    onDeleteSearchResultAction,
    origin,
    primaryAction,
    secondaryAction,
    tertiaryAction,
    wasProcessed,
  });

  const openPreview = useCallback(
    () => {
      onOpenDetail?.({ origin });
    },
    [onOpenDetail, origin],
  );
  const handleCardPreview = useCallback(() => {
    if (suppressCardPreviewRef.current) {
      return;
    }

    openPreview();
  }, [openPreview]);
  const previewAction: ResolvedOriginAction | null = canOpenDetail
    ? {
        key: "preview",
        label: "Preview",
        humanReadable: "Preview source material",
        icon: "preview",
        onClick: openPreview,
      }
    : null;
  const actionMenuItems = previewAction
    ? [previewAction, ...resolvedActions]
    : resolvedActions;
  const desktopInlineActions = actionMenuItems.filter((action) =>
    DESKTOP_INLINE_ACTION_KEYS.has(action.key),
  );
  const desktopOverflowActions = actionMenuItems.filter(
    (action) => !DESKTOP_INLINE_ACTION_KEYS.has(action.key),
  );
  const {
    handleCardClick,
    handleCardKeyDown,
    handlePointerDown,
    handlePointerMove,
  } = useOriginItemGestures({
    canOpenDetail,
    hasActions: actionMenuItems.length > 0,
    isMobileDevice,
    onOpenDetail: handleCardPreview,
  });

  const swipeAction = canOpenDetail
    ? {
        onSwipe: openPreview,
        icon: "preview" as GenericIconType,
        humanReadable: "Preview source material",
        actionDescription: "Preview",
      }
    : primaryAction && !primaryAction.hide
      ? {
          onSwipe: () => primaryAction.onClick(origin),
          icon: primaryAction.icon,
          color: primaryAction.color,
          humanReadable: primaryAction.humanReadable,
          actionDescription: primaryAction.actionDescription ?? "Shape",
        }
      : undefined;

  const desktopActionsSection = (
    <HStack
      display={{ base: "none", lg: "flex" }}
      gap="2"
      flexWrap="wrap"
      justify="flex-start"
      flexDirection="row-reverse"
      flexShrink={0}
    >
      {desktopInlineActions.map((action) => {
        return (
          <OriginActionIconButton
            key={action.key}
            action={action}
            onInteraction={stopCardTogglePropagation}
          />
        );
      })}
      {desktopOverflowActions.length > 0 ? (
        <Box
          onClick={stopCardTogglePropagation}
          onPointerDown={stopCardTogglePropagation}
          onKeyDown={stopCardTogglePropagation}
        >
          <WithMenu
            placement="bottom-end"
            size="sm"
            onOpenChange={handleActionMenuOpenChange}
            trigger={
              <Button
                variant="ghost"
                size="xs"
                h="8"
                minW="9"
                px="0"
                aria-label="More source material actions"
                title="More source material actions"
                color="moveon.surface.fg"
                _hover={{
                  bg: "moveon.surface.elevatedBg",
                }}
              >
                <GenericIcon
                  icon="three_dots_vertical"
                  size={iconSizePresets.inline}
                  humanReadable="More source material actions"
                />
              </Button>
            }
          >
            {desktopOverflowActions.map((action) => (
              <WithMenuItem
                key={action.key}
                value={action.key}
                humanReadable={action.humanReadable}
                onClick={() => {
                  void action.onClick();
                }}
                tone={action.tone === "danger" ? "error" : undefined}
              >
                {renderMenuLabel(
                  action.label,
                  action.icon,
                  getOriginActionColor(action),
                )}
              </WithMenuItem>
            ))}
          </WithMenu>
        </Box>
      ) : null}
    </HStack>
  );

  const mobileActionsSection =
    actionMenuItems.length > 0 ? (
      <Box
        display={{ base: "block", lg: "none" }}
        flexShrink={0}
        onClick={stopCardTogglePropagation}
        onPointerDown={stopCardTogglePropagation}
        onKeyDown={stopCardTogglePropagation}
      >
        <WithMenu
          placement="bottom-end"
          size="sm"
          onOpenChange={handleActionMenuOpenChange}
          trigger={
            <Button
              variant="ghost"
              size="xs"
              h="6"
              minW="6"
              w="6"
              px="0"
              aria-label="Source material actions"
              title="Source material actions"
              color="moveon.surface.fg"
              _hover={{
                bg: "moveon.surface.elevatedBg",
              }}
            >
              <GenericIcon
                icon="three_dots_vertical"
                size={iconSizePresets.inline}
                humanReadable="Source material actions"
              />
            </Button>
          }
        >
          {actionMenuItems.map((action) => (
            <WithMenuItem
              key={action.key}
              value={action.key}
              humanReadable={action.humanReadable}
              onClick={() => {
                void action.onClick();
              }}
              tone={action.tone === "danger" ? "error" : undefined}
            >
              {renderMenuLabel(
                action.label,
                action.icon,
                getOriginActionColor(action),
              )}
            </WithMenuItem>
          ))}
        </WithMenu>
      </Box>
    ) : null;

  const headerSection = (
    <HStack justify="space-between" align="start" gap="3">
      <HStack align="center" gap="2" flex="1" minW={0} flexWrap="wrap">
        <Badge
          variant="surface"
          colorPalette={statusPalette}
          flexShrink={0}
        >
          {statusLabel}
        </Badge>
        <Text
          fontSize="xs"
          fontWeight="medium"
          color="moveon.text.muted"
          lineHeight="short"
        >
          {formattedTimestamp}
        </Text>
      </HStack>

      <HStack gap="2" flexShrink={0}>
        {mobileActionsSection}
        {desktopActionsSection}
      </HStack>
    </HStack>
  );

  const card = (
    <ContentItemCard
      role={canOpenDetail ? "button" : undefined}
      tabIndex={canOpenDetail ? 0 : undefined}
      selected={isSelected}
      bg={backgroundColor}
      borderWidth="1"
      cursor={canOpenDetail ? "pointer" : "default"}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      _hover={CONTENT_ITEM_HOVER_STYLES}
      _focusVisible={
        canOpenDetail ? CONTENT_ITEM_FOCUS_VISIBLE_STYLES : undefined
      }
    >
      <HStack align="stretch" gap={{ base: "3", md: "4" }}>
        <Box position="relative" w="5" flexShrink={0} aria-hidden="true">
          <Box
            position="absolute"
            top="1"
            bottom="1"
            left="50%"
            w="1"
            bg="moveon.surface.border"
            transform="translateX(-50%)"
          />
          <Box
            position="absolute"
            top="5"
            left="50%"
            transform="translate(-50%, -50%)"
            boxSize="3"
            borderRadius="full"
            bg={railColor}
            borderWidth="2"
            borderColor={backgroundColor}
          />
        </Box>

        <VStack align="stretch" gap="3" flex="1" minW={0}>
          {headerSection}
          <OriginItemPreview
            config={config}
            hit={hit}
            isInSearchContext={isInSearchContext}
            origin={origin}
          />
        </VStack>
      </HStack>
    </ContentItemCard>
  );

  const selectableCard = selection ? (
    <WithSwipeSelector
      isMobile={isMobileDevice}
      selection={{
        isSelected: selection.isSelected,
        onToggle: selection.onToggle,
        humanReadable: selection.humanReadable,
      }}
      action={swipeAction}
      visual={{
        uncoveredBackgroundColor: backgroundColor,
      }}
    >
      {card}
    </WithSwipeSelector>
  ) : (
    card
  );

  return selectableCard;
}

export default memo(OriginItem);
